import React, { useState, useMemo, useEffect } from 'react';
import { Expense } from '../types/Expense';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Typography, Grid, Select, MenuItem, FormControl, InputLabel, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, IconButton, Paper, Tooltip, Card, CardContent, Chip, Checkbox, FormControlLabel, Divider, CircularProgress } from '@mui/material';
import {
  PictureAsPdf,
  InsertDriveFile,
  CalendarToday,
  Label,
  AlignHorizontalLeft,
  Euro,
  Person,
  SwapHoriz,
  Sort,
  ArrowDropUp,
  ArrowDropDown,
  AccountBalanceWallet,
  TrendingDown,
  TrendingUp,
  PieChart as PieChartIcon,
  AttachMoney,
  PersonOutline
} from '@mui/icons-material';
import { 
  ResponsiveContainer, Tooltip as RechartsTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area, Cell
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import CustomChip from './CustomChip';
import { designTokens } from '../theme/designTokens';

interface ExpenseReportProps {
  expenses: Expense[];
  payers: string[];
  payerColors: { [key: string]: string };
  onUpdateExpense: (updatedExpense: Expense) => Promise<void>;
}

type SortableExpenseKey = keyof Omit<Expense, 'id' | 'fileUrl'> | 'type' | 'sharedWithOrDebtFrom';

type SortConfig = {
  key: SortableExpenseKey;
  direction: 'asc' | 'desc';
} | null;

const ExpenseReport: React.FC<ExpenseReportProps> = ({ expenses, payers, payerColors, onUpdateExpense }) => {
  const intl = useIntl();
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedPayers, setSelectedPayers] = useState<string[]>(payers);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'date', direction: 'asc' });
  const [isLoading, setIsLoading] = useState(true);
  const [evolutionPayers, setEvolutionPayers] = useState<string[]>(payers.slice(0, 2));

  const months = Array.from({ length: 12 }, (_, i) => i);
  const years = Array.from(new Set(expenses.map(e => new Date(e.date).getFullYear()))).sort((a, b) => b - a);

  const renderPayerChip = (payer: string) => (
    <CustomChip
      label={payer}
      color={payerColors[payer] || '#E0E0E0'}
      size="small"
    />
  );

  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getMonth() === selectedMonth &&
        expenseDate.getFullYear() === selectedYear &&
        (selectedPayers.includes(expense.payer) ||
          (expense.shared && selectedPayers.includes(expense.sharedWith || '')) ||
          (expense.isDebt && selectedPayers.includes(expense.debtFrom || '')));
    });
  }, [expenses, selectedMonth, selectedYear, selectedPayers]);

  const sortedExpenses = useMemo(() => {
    let sortableExpenses = filteredExpenses.filter(expense =>
      expense.shared &&
      expense.sharedWith &&
      selectedPayers.includes(expense.payer) &&
      selectedPayers.includes(expense.sharedWith)
    );

    if (sortConfig !== null) {
      sortableExpenses.sort((a, b) => {
        let aValue: any = a[sortConfig.key as keyof Expense];
        let bValue: any = b[sortConfig.key as keyof Expense];

        if (sortConfig.key === 'type') {
          aValue = a.isDebt ? 'debt' : a.shared ? 'shared' : '-';
          bValue = b.isDebt ? 'debt' : b.shared ? 'shared' : '-';
        } else if (sortConfig.key === 'sharedWithOrDebtFrom') {
          aValue = a.shared ? a.sharedWith : a.isDebt ? a.debtFrom : '-';
          bValue = b.shared ? b.sharedWith : b.isDebt ? b.debtFrom : '-';
        }

        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableExpenses;
  }, [filteredExpenses, sortConfig, selectedPayers]);

  const requestSort = (key: SortableExpenseKey) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const calculateDebts = () => {
    const debts: Record<string, Record<string, number>> = {};
    selectedPayers.forEach(payer => {
      debts[payer] = {};
      selectedPayers.forEach(otherPayer => {
        if (payer !== otherPayer) {
          debts[payer][otherPayer] = 0;
        }
      });
    });

    filteredExpenses.forEach(expense => {
      if (expense.shared && expense.sharedWith && selectedPayers.includes(expense.payer) && selectedPayers.includes(expense.sharedWith)) {
        const halfAmount = expense.amount / 2;
        debts[expense.sharedWith][expense.payer] += halfAmount;
        debts[expense.payer][expense.sharedWith] -= halfAmount;
      }
      if (expense.isDebt && expense.debtFrom && selectedPayers.includes(expense.payer) && selectedPayers.includes(expense.debtFrom)) {
        debts[expense.payer][expense.debtFrom] += expense.amount;
      }
    });

    return debts;
  };

  const debtSummary = useMemo(() => {
    const debts = calculateDebts();
    return Object.entries(debts)
      .flatMap(([payer, owed]) =>
        Object.entries(owed)
          .filter(([_, amount]) => amount > 0)
          .map(([owedTo, amount]) => ({
            from: payer,
            to: owedTo,
            amount: amount
          }))
      )
      .sort((a, b) => b.amount - a.amount);
  }, [filteredExpenses, payers]);

  const totalDebt = useMemo(() => {
    return debtSummary.reduce((sum, debt) => sum + debt.amount, 0);
  }, [debtSummary]);

  const kpis = useMemo(() => {
    const total = sortedExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    const p1 = selectedPayers[0];
    const p2 = selectedPayers[1];

    const getShare = (p: string) => sortedExpenses.reduce((sum, exp) => {
      if (exp.payer === p || exp.sharedWith === p) {
        return sum + (exp.amount / 2);
      }
      return sum;
    }, 0);

    return {
      total,
      share1: p1 ? getShare(p1) : 0,
      share2: p2 ? getShare(p2) : 0,
      payer1: p1,
      payer2: p2
    };
  }, [sortedExpenses, selectedPayers]);

  const debtEvolution = useMemo(() => {
    const data = [];
    const p1 = evolutionPayers[0];
    const p2 = evolutionPayers[1];
    
    if (!p1 || !p2) return [];

    const now = new Date(selectedYear, selectedMonth);
    
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = d.getMonth();
      const y = d.getFullYear();
      
      const monthExpenses = expenses.filter(e => {
        const ed = new Date(e.date);
        return ed.getMonth() === m && ed.getFullYear() === y && (e.shared || e.isDebt);
      });
      
      let balance = 0;
      monthExpenses.forEach(e => {
        if (e.shared && e.sharedWith) {
          if (e.payer === p1 && e.sharedWith === p2) balance += e.amount / 2;
          if (e.payer === p2 && e.sharedWith === p1) balance -= e.amount / 2;
        }
        if (e.isDebt && e.debtFrom) {
          if (e.payer === p1 && e.debtFrom === p2) balance += e.amount;
          if (e.payer === p2 && e.debtFrom === p1) balance -= e.amount;
        }
      });
      
      data.push({
        name: intl.formatMessage({ id: `month.${m}` }).substring(0, 3),
        debt: Number(balance.toFixed(2)),
        fullMonth: intl.formatMessage({ id: `month.${m}` }) + ' ' + y
      });
    }
    return data;
  }, [expenses, selectedMonth, selectedYear, evolutionPayers, intl]);

  const generatePDF = () => {
    const doc = new jsPDF();
    doc.text('Expense Report', 14, 15);
    doc.setFontSize(12);
    doc.text(`Month: ${intl.formatMessage({ id: `month.${selectedMonth}` })}`, 14, 25);
    doc.text(`Year: ${selectedYear}`, 14, 32);

    doc.setFontSize(14);
    // @ts-ignore
    doc.setFont('', 'bold');
    doc.text('Debt Summary:', 14, 42);
    // @ts-ignore
    doc.setFont('', 'normal');
    doc.setFontSize(12);

    let yPos = 48;
    debtSummary.forEach((debt, index) => {
      if (index === 0) {
        doc.setTextColor(255, 0, 0);
      }
      doc.text(`${debt.from} owes ${debt.to}: €${debt.amount.toFixed(2)}`, 14, yPos);
      if (index === 0) {
        doc.setTextColor(0, 0, 0);
      }
      yPos += 6;
    });

    const tableColumn = [
      intl.formatMessage({ id: 'category' }),
      intl.formatMessage({ id: 'description' }),
      intl.formatMessage({ id: 'date' }),
      intl.formatMessage({ id: 'amount' }),
      ...selectedPayers,
      intl.formatMessage({ id: 'payer' }),
      intl.formatMessage({ id: 'shared' })
    ];
    const tableRows = sortedExpenses.map(expense => [
      expense.category,
      expense.description,
      new Date(expense.date).toLocaleDateString(),
      `€${expense.amount.toFixed(2)}`,
      ...selectedPayers.map(p =>
        (expense.payer === p || expense.sharedWith === p)
          ? `€${(expense.amount / 2).toFixed(2)}`
          : '-'
      ),
      expense.payer,
      expense.isDebt ? intl.formatMessage({ id: 'debt' }) : expense.shared ? intl.formatMessage({ id: 'shared' }) : '-'
    ]);

    // Add total row to PDF
    const totalAmount = sortedExpenses.reduce((sum, exp) => sum + exp.amount, 0);
    const totalsPerRow = selectedPayers.map(p => {
      const total = sortedExpenses.reduce((sum, exp) => {
        if (exp.payer === p || exp.sharedWith === p) {
          return sum + (exp.amount / 2);
        }
        return sum;
      }, 0);
      return `€${total.toFixed(2)}`;
    });

    tableRows.push([
      intl.formatMessage({ id: 'total' }),
      '',
      '',
      `€${totalAmount.toFixed(2)}`,
      ...totalsPerRow,
      '',
      ''
    ]);

    (doc as any).autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: yPos + 10
    });

    doc.save('expense_report.pdf');
  };

  const generateCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8,"
      + "\uFEFF" // BOM for special characters
      + `${intl.formatMessage({ id: 'category' })},${intl.formatMessage({ id: 'description' })},${intl.formatMessage({ id: 'date' })},${intl.formatMessage({ id: 'amount' })},${selectedPayers.join(',')},${intl.formatMessage({ id: 'payer' })},${intl.formatMessage({ id: 'shared' })}\n`
      + sortedExpenses.map(expense => {
        const payerShares = selectedPayers.map(p =>
          (expense.payer === p || expense.sharedWith === p)
            ? `€${(expense.amount / 2).toFixed(2)}`
            : '-'
        ).join(',');
        return `"${expense.category}","${expense.description}","${new Date(expense.date).toLocaleDateString()}","€${expense.amount.toFixed(2)}",${payerShares},"${expense.payer}","${expense.isDebt ? intl.formatMessage({ id: 'debt' }) : expense.shared ? intl.formatMessage({ id: 'shared' }) : '-'}"`;
      }).join("\n")
      + `\n${intl.formatMessage({ id: 'total' })},,,€${sortedExpenses.reduce((sum, exp) => sum + exp.amount, 0).toFixed(2)},${selectedPayers.map(p => {
        const total = sortedExpenses.reduce((sum, exp) => {
          if (exp.payer === p || exp.sharedWith === p) {
            return sum + (exp.amount / 2);
          }
          return sum;
        }, 0);
        return `€${total.toFixed(2)}`;
      }).join(',')},,`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "expense_report.csv");
    document.body.appendChild(link);
    link.click();
  };

  const handlePayerSelection = (payer: string) => {
    setSelectedPayers(prev =>
      prev.includes(payer)
        ? prev.filter(p => p !== payer)
        : [...prev, payer]
    );
  };




  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
      width: '100%',
      height: '100%',
      overflow: 'auto',
      mt: 2
    }}>
      <Paper
        sx={{
          p: 3,
          borderRadius: designTokens.borderRadius.lg,
          background: designTokens.colors.background.paper,
          border: designTokens.glassmorphism.border,
          boxShadow: designTokens.shadows.md
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: designTokens.typography.fontWeight.bold, mb: 2 }}>
          <FormattedMessage id="filter" defaultMessage="Filter Report" />
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="month-select-label">
                <FormattedMessage id="month" />
              </InputLabel>
              <Select
                labelId="month-select-label"
                value={selectedMonth}
                label={intl.formatMessage({ id: 'month' })}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                sx={{ borderRadius: designTokens.borderRadius.md }}
              >
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {intl.formatMessage({ id: `month.${month}` })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="year-select-label">
                <FormattedMessage id="year" />
              </InputLabel>
              <Select
                labelId="year-select-label"
                value={selectedYear}
                label={intl.formatMessage({ id: 'year' })}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                sx={{ borderRadius: designTokens.borderRadius.md }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1, bgcolor: `${designTokens.colors.background.elevated}33`, borderRadius: designTokens.borderRadius.md }}>
              {payers.map(payer => (
                <FormControlLabel
                  key={payer}
                  control={
                    <Checkbox
                      checked={selectedPayers.includes(payer)}
                      onChange={() => handlePayerSelection(payer)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{payer}</Typography>}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: designTokens.borderRadius.lg, 
            background: `linear-gradient(135deg, ${designTokens.colors.primary.main}11 0%, ${designTokens.colors.primary.main}22 100%)`,
            border: `1px solid ${designTokens.colors.primary.main}33`,
            boxShadow: designTokens.shadows.sm
          }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachMoney sx={{ color: designTokens.colors.primary.main, fontSize: 20 }} />
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: designTokens.colors.text.secondary, textTransform: 'uppercase' }}>
                  <FormattedMessage id="total" />
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: designTokens.colors.primary.main }}>
                €{kpis.total.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: designTokens.borderRadius.lg, 
            background: designTokens.colors.background.paper,
            border: designTokens.glassmorphism.border,
            boxShadow: designTokens.shadows.sm
          }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonOutline sx={{ color: designTokens.colors.success.main, fontSize: 20 }} />
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: designTokens.colors.text.secondary, textTransform: 'uppercase' }}>
                  {kpis.payer1 || 'User 1'}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                €{kpis.share1.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: designTokens.borderRadius.lg, 
            background: designTokens.colors.background.paper,
            border: designTokens.glassmorphism.border,
            boxShadow: designTokens.shadows.sm
          }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonOutline sx={{ color: designTokens.colors.warning.main, fontSize: 20 }} />
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: designTokens.colors.text.secondary, textTransform: 'uppercase' }}>
                  {kpis.payer2 || 'User 2'}
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                €{kpis.share2.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            borderRadius: designTokens.borderRadius.lg, 
            background: totalDebt > 0 ? `${designTokens.colors.error.main}11` : `${designTokens.colors.success.main}11`,
            border: `1px solid ${totalDebt > 0 ? designTokens.colors.error.main : designTokens.colors.success.main}33`,
            boxShadow: designTokens.shadows.sm
          }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccountBalanceWallet sx={{ color: totalDebt > 0 ? designTokens.colors.error.main : designTokens.colors.success.main, fontSize: 20 }} />
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: designTokens.colors.text.secondary, textTransform: 'uppercase' }}>
                  <FormattedMessage id="totalDebt" />
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: totalDebt > 0 ? designTokens.colors.error.main : designTokens.colors.success.main }}>
                €{totalDebt.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 1 }}>
        <Button
          variant="contained"
          startIcon={<PictureAsPdf />}
          onClick={generatePDF}
          sx={{
            background: 'linear-gradient(135deg, #FF5252 0%, #D32F2F 100%)',
            color: 'white',
            borderRadius: designTokens.borderRadius.md,
            textTransform: 'none',
            px: 2,
            boxShadow: designTokens.shadows.sm,
            '&:hover': {
              background: 'linear-gradient(135deg, #FF1744 0%, #C62828 100%)',
              boxShadow: designTokens.shadows.md,
            }
          }}
        >
          <FormattedMessage id="exportAsPDF" />
        </Button>
        <Button
          variant="contained"
          startIcon={<InsertDriveFile />}
          onClick={generateCSV}
          sx={{
            background: 'linear-gradient(135deg, #66BB6A 0%, #43A047 100%)',
            color: 'white',
            borderRadius: designTokens.borderRadius.md,
            textTransform: 'none',
            px: 2,
            boxShadow: designTokens.shadows.sm,
            '&:hover': {
              background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
              boxShadow: designTokens.shadows.md,
            }
          }}
        >
          <FormattedMessage id="exportAsCSV" />
        </Button>
      </Box>

      <Grid container spacing={3}>

        <Grid item xs={12}>
          <Card
            sx={{
              height: '100%',
              borderRadius: designTokens.borderRadius.lg,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.md
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AccountBalanceWallet sx={{ color: designTokens.colors.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
                  <FormattedMessage id="debtSummary" />
                </Typography>
              </Box>

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: `${designTokens.colors.background.elevated}33` }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: designTokens.colors.text.secondary }}><FormattedMessage id="debtor" /></TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: designTokens.colors.text.secondary }}><FormattedMessage id="creditor" /></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: designTokens.colors.text.secondary }}><FormattedMessage id="amount" /></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {debtSummary.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: designTokens.colors.text.secondary }}>
                          No debts found for this period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      debtSummary.map((debt, index) => (
                        <TableRow key={index} sx={{ '&:hover': { bgcolor: `${designTokens.colors.background.elevated}22` } }}>
                          <TableCell>
                            {renderPayerChip(debt.from)}
                          </TableCell>
                          <TableCell>
                            {renderPayerChip(debt.to)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.error.main }}>
                              €{debt.amount.toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 3, opacity: 0.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDown sx={{ color: designTokens.colors.error.main }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: designTokens.typography.fontWeight.semibold }}>
                    <FormattedMessage id="totalDebt" />
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.error.main }}>
                  €{totalDebt.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card
            sx={{
              height: '100%',
              borderRadius: designTokens.borderRadius.lg,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.md
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TrendingUp sx={{ color: designTokens.colors.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
                    <FormattedMessage id="debtSummary" /> (12 <FormattedMessage id="months" />)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {payers.map(payer => (
                    <FormControlLabel
                      key={payer}
                      control={
                        <Checkbox
                          checked={evolutionPayers.includes(payer)}
                          onChange={() => {
                            setEvolutionPayers(prev => {
                              if (prev.includes(payer)) {
                                return prev.filter(p => p !== payer);
                              }
                              if (prev.length >= 2) {
                                return [prev[1], payer];
                              }
                              return [...prev, payer];
                            });
                          }}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">{payer}</Typography>}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ height: 350, width: '100%' }}>
                {debtEvolution.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography color="textSecondary">No evolution data</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={debtEvolution} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={designTokens.colors.background.elevated} />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: designTokens.colors.text.secondary, fontSize: 12 }} 
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: designTokens.colors.text.secondary, fontSize: 12 }}
                        tickFormatter={(value) => `€${value}`}
                      />
                      <RechartsTooltip 
                        formatter={(value: number) => [`€${value.toFixed(2)}`, evolutionPayers[0] || 'User']}
                        labelFormatter={(label, payload) => payload[0]?.payload?.fullMonth || label}
                        contentStyle={{ 
                          borderRadius: designTokens.borderRadius.md,
                          border: 'none',
                          boxShadow: designTokens.shadows.md,
                          background: designTokens.colors.background.paper
                        }}
                      />
                      <Bar 
                        dataKey="debt" 
                        radius={[4, 4, 0, 0]}
                        fill={designTokens.colors.primary.main}
                      >
                        {debtEvolution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.debt >= 0 ? designTokens.colors.success.main : designTokens.colors.error.main} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: designTokens.borderRadius.lg,
              background: designTokens.colors.background.paper,
              border: designTokens.glassmorphism.border,
              boxShadow: designTokens.shadows.md,
              maxHeight: 600,
              overflow: 'auto'
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { id: 'category', icon: Label, label: 'category' },
                    { id: 'description', icon: AlignHorizontalLeft, label: 'description' },
                    { id: 'date', icon: CalendarToday, label: 'date' },
                    { id: 'amount', icon: Euro, label: 'amount' },
                    ...selectedPayers.map(p => ({ id: `payer_${p}`, label: p, isPayer: true })),
                    { id: 'payer', icon: Person, label: 'payer' },
                    { id: 'type', icon: SwapHoriz, label: 'shared' },
                  ].map(column => (
                    <TableCell
                      key={column.id}
                      sx={{
                        fontWeight: 'bold',
                        bgcolor: `${designTokens.colors.background.paper}dd`,
                        backdropFilter: 'blur(10px)',
                        color: designTokens.colors.text.secondary
                      }}
                    >
                      <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => !('isPayer' in column) && requestSort(column.id as SortableExpenseKey)}>
                        {'icon' in column && <column.icon sx={{ marginRight: '8px', fontSize: 18, color: designTokens.colors.text.secondary }} />}
                        {'isPayer' in column ? column.label : <FormattedMessage id={column.label as string} />}
                        {sortConfig?.key === column.id ? (
                          sortConfig.direction === 'asc' ? <ArrowDropUp /> : <ArrowDropDown />
                        ) : (
                          !('isPayer' in column) && <Sort sx={{ fontSize: 16, ml: 0.5, opacity: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedExpenses.map(expense => (
                  <TableRow
                    key={expense.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: `${designTokens.colors.background.elevated}22`,
                        '& .share-cell': {
                          bgcolor: `${designTokens.colors.primary.main}11`,
                        }
                      },
                      bgcolor: expense.isDebt ? `${designTokens.colors.error.main}08` : 'transparent',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      {expense.isDebt && <TrendingDown sx={{ fontSize: 16, mr: 0.5, color: designTokens.colors.error.main, verticalAlign: 'middle' }} />}
                      {expense.category}
                    </TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33`, fontWeight: designTokens.typography.fontWeight.medium }}>{expense.description}</TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>{new Date(expense.date).toLocaleDateString()}</TableCell>
                    <TableCell align="right" sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33`, fontWeight: designTokens.typography.fontWeight.bold }}>
                      €{expense.amount.toFixed(2)}
                    </TableCell>
                    {selectedPayers.map(p => (
                      <TableCell 
                        key={p} 
                        align="right" 
                        className="share-cell"
                        sx={{ 
                          borderBottom: `1px solid ${designTokens.colors.background.elevated}33`, 
                          fontWeight: designTokens.typography.fontWeight.bold, 
                          color: (expense.payer === p || expense.sharedWith === p) ? designTokens.colors.primary.main : designTokens.colors.text.disabled,
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        {(expense.payer === p || expense.sharedWith === p) ? `€${(expense.amount / 2).toFixed(2)}` : '-'}
                      </TableCell>
                    ))}
                    <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      {renderPayerChip(expense.payer)}
                    </TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${designTokens.colors.background.elevated}33` }}>
                      <Tooltip title={`Shared with ${expense.sharedWith}`}>
                        <Chip
                          label={intl.formatMessage({ id: expense.isDebt ? 'debt' : 'shared' })}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            bgcolor: expense.isDebt ? `${designTokens.colors.error.main}22` : `${designTokens.colors.primary.main}22`,
                            color: expense.isDebt ? designTokens.colors.error.main : designTokens.colors.primary.main,
                            fontWeight: 'bold'
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: `${designTokens.colors.background.elevated}11` }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                    <FormattedMessage id="total" />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    €{sortedExpenses.reduce((sum, exp) => sum + exp.amount, 0).toFixed(2)}
                  </TableCell>
                  {selectedPayers.map(p => {
                    const personTotal = sortedExpenses.reduce((sum, exp) => {
                      if (exp.payer === p || exp.sharedWith === p) {
                        return sum + (exp.amount / 2);
                      }
                      return sum;
                    }, 0);
                    return (
                      <TableCell key={p} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: designTokens.colors.primary.main }}>
                        €{personTotal.toFixed(2)}
                      </TableCell>
                    );
                  })}
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExpenseReport;